// ============================================================================
// ENGLISH TRANSLATIONS - CHAPTER 1: GENESIS
// ============================================================================
// Levels 1-13 (13 levels, 39 questions)

export const chapter1Translations = {
  name: "Genesis",
  description: "In the beginning... Knowledge allows the spirit to be vivified",
  
  spiritualWisdom: {
    principle: "Knowledge allows the spirit to be vivified",
    scripture: "My people are destroyed for lack of knowledge (Hosea 4:6)",
    application: "Each verse studied, each truth discovered nourishes our soul and strengthens our faith"
  },
  
  levels: {
    1: {
      name: "The Creation",
      challenge: "In the beginning, God created the heavens and the earth...",
      easy: {
        question: "In how many days did God create the world?",
        options: ["5 days", "6 days", "7 days", "8 days"],
        hint: "Think of the day of rest in the week",
        funFact: "💡 On the 7th day, God rested. This is the origin of the Sabbath, the weekly day of rest!"
      },
      medium: {
        question: "What did God create on the fourth day?",
        options: ["Animals", "Man", "The sun, moon and stars", "Plants"],
        hint: "These creations light the earth and mark time",
        funFact: "🌟 The luminaries were created to mark the seasons and religious festivals!"
      },
      hard: {
        question: "What does it mean that man was created 'in the image of God'?",
        options: [
          "Identical physical appearance",
          "Ability to create and love",
          "Natural immortality",
          "Divine power"
        ],
        hint: "It's about spiritual and intellectual qualities",
        funFact: "✨ The image of God in us includes creativity, moral conscience and the ability to love!"
      }
    },
    
    2: {
      name: "The Temptation in the Garden",
      challenge: "The serpent was the most cunning of all the animals...",
      easy: {
        question: "Which animal tempted Eve in the garden?",
        options: ["The lion", "The serpent", "The eagle", "The dragon"],
        hint: "This animal crawls on its belly",
        funFact: "🐍 The serpent represents temptation and cunning throughout the Bible!"
      },
      medium: {
        question: "What false promise did the serpent make to Eve?",
        options: [
          "You will be immortal",
          "You will be rich",
          "You will be like gods",
          "You will be happy"
        ],
        hint: "The serpent promises elevation to divine level",
        funFact: "🎭 The serpent's lie mixes truth and deception - classic temptation technique!"
      },
      hard: {
        question: "How does this fall illuminate our struggle between good and evil?",
        options: [
          "Evil is stronger",
          "Free will implies responsibility",
          "God abandons us",
          "Temptation is invincible"
        ],
        hint: "It's about our ability to choose and bear the consequences",
        funFact: "🌳 The tree of knowledge symbolizes the moral choice that defines humanity!"
      }
    },
    
    3: {
      name: "Cain and Abel",
      challenge: "Two brothers, two offerings, an eternal drama...",
      easy: {
        question: "Who was the first murderer in biblical history?",
        options: ["Abel", "Seth", "Cain", "Adam"],
        hint: "Abel's jealous brother",
        funFact: "⚡ Cain becomes the symbol of destructive fraternal jealousy!"
      },
      medium: {
        question: "Why did God prefer Abel's offering?",
        options: [
          "Greater quantity",
          "Abel was more handsome",
          "Offered with a sincere heart",
          "It was meat"
        ],
        hint: "God looks at the heart",
        funFact: "❤️ God looks at the heart, not just the appearance of our actions!"
      },
      hard: {
        question: "What does this story teach us about jealousy?",
        options: [
          "Jealousy is natural",
          "Uncontrolled jealousy leads to destruction",
          "God favors some",
          "Competition is healthy"
        ],
        hint: "It's about the consequences of not controlling our emotions",
        funFact: "🔥 'Sin is crouching at your door' - God warns us before the fall!"
      }
    },

    4: {
      name: "Noah's Flood",
      challenge: "The rain fell on the earth forty days and forty nights...",
      easy: {
        question: "How many days and nights did the flood rain last?",
        options: ["30 days", "40 days", "50 days", "100 days"],
        hint: "A symbolic number in the Bible",
        funFact: "☔ The number 40 symbolizes trial and transformation in the Bible!"
      },
      medium: {
        question: "How many clean animals was Noah to take on the ark?",
        options: [
          "1 pair",
          "2 pairs",
          "7 pairs",
          "10 pairs"
        ],
        hint: "More than unclean animals",
        funFact: "🦁 Clean animals were destined for sacrifices after the flood!"
      },
      hard: {
        question: "What does the ark symbolize in our spiritual journey?",
        options: [
          "Fleeing from problems",
          "Salvation through faith and obedience",
          "Necessary isolation",
          "Believers' superiority"
        ],
        hint: "It's about salvation offered to those who believe",
        funFact: "🚢 The ark prefigures salvation: one path to escape judgment!"
      }
    },

    5: {
      name: "The Tower of Babel",
      challenge: "Let us build ourselves a tower with its top in the heavens...",
      easy: {
        question: "What did God confuse at Babel?",
        options: ["Plans", "Languages", "Tools", "Thoughts"],
        hint: "What allows communication",
        funFact: "🗣️ Babel is the mythical origin of world linguistic diversity!"
      },
      medium: {
        question: "Why did people want to build this tower?",
        options: [
          "To protect themselves",
          "To live together",
          "To make a name and reach heaven",
          "To honor God"
        ],
        hint: "It's about pride and wanting to equal God",
        funFact: "🏗️ The pride of wanting to equal God always leads to confusion!"
      },
      hard: {
        question: "How does Babel's pride manifest today?",
        options: [
          "In technology only",
          "In human will for absolute autonomy without God",
          "In skyscrapers",
          "In different languages"
        ],
        hint: "It's about wanting to do without God",
        funFact: "💭 Babel reminds us that unity without God becomes destructive pride!"
      }
    },

    6: {
      name: "The Call of Abraham",
      challenge: "Leave your country, your people and your father's household...",
      easy: {
        question: "How old was Abraham when he left his country?",
        options: ["50 years", "65 years", "75 years", "100 years"],
        hint: "A very advanced age to start over",
        funFact: "🚶 At 75, Abraham begins a new life - it's never too late!"
      },
      medium: {
        question: "What threefold promise does God make to Abraham?",
        options: [
          "Wealth, power, wisdom",
          "Land, offspring, universal blessing",
          "Long life, peace, prosperity",
          "Faith, hope, love"
        ],
        hint: "A promise that concerns all humanity",
        funFact: "🌟 The promise to Abraham concerns all humanity, not just him!"
      },
      hard: {
        question: "What does Abraham's 'leap of faith' teach us?",
        options: [
          "Blind faith is dangerous",
          "Following God requires letting go of our securities",
          "God favors certain peoples",
          "Faith guarantees success"
        ],
        hint: "It's about trusting while abandoning our landmarks",
        funFact: "✨ Abraham is called 'father of faith' because he believed without seeing!"
      }
    },

    7: {
      name: "The Sacrifice of Isaac",
      challenge: "Take your son, your only one, whom you love...",
      easy: {
        question: "On which mountain was Abraham to sacrifice Isaac?",
        options: ["Sinai", "Moriah", "Carmel", "Zion"],
        hint: "This mountain will become a sacred place",
        funFact: "🏔️ Mount Moriah will become the site of the Temple of Jerusalem!"
      },
      medium: {
        question: "What did God provide in place of Isaac?",
        options: [
          "A lamb",
          "A ram caught by its horns",
          "A goat",
          "A bull"
        ],
        hint: "An adult male animal with horns",
        funFact: "🐏 The substituted ram prefigures the ultimate substitutionary sacrifice!"
      },
      hard: {
        question: "How does this test illustrate absolute faith?",
        options: [
          "God tests cruelly",
          "Faith transcends even paternal love",
          "Human sacrifices please God",
          "Abraham didn't really love Isaac"
        ],
        hint: "It's about trusting even when we don't understand",
        funFact: "💝 God always provides a way out when we trust Him!"
      }
    },

    8: {
      name: "Jacob and Esau",
      challenge: "The younger will serve the elder...",
      easy: {
        question: "For what did Esau sell his birthright?",
        options: ["Gold", "A bowl of lentils", "Clothes", "Wine"],
        hint: "A dish to satisfy his hunger",
        funFact: "🍲 A moment of hunger cost Esau his eternal blessing!"
      },
      medium: {
        question: "How did Jacob deceive his father Isaac?",
        options: [
          "By changing his voice",
          "By disguising himself",
          "By covering himself with goat skin",
          "By lying about his name"
        ],
        hint: "To resemble his hairy brother",
        funFact: "🎭 Isaac, blind, recognized the smell and touch but not the deception!"
      },
      hard: {
        question: "What does this story reveal about our choices?",
        options: [
          "Deception is sometimes necessary",
          "Immediate desires can cost our destiny",
          "God approves of cunning",
          "The younger is always favored"
        ],
        hint: "It's about the consequences of our impulsive decisions",
        funFact: "⚖️ Our impulsive choices can have eternal consequences!"
      }
    },

    9: {
      name: "Jacob's Dream",
      challenge: "He saw a ladder set up on the earth, with its top reaching to heaven...",
      easy: {
        question: "What did Jacob see in his dream?",
        options: [
          "A giant tree",
          "A ladder to heaven with angels",
          "A temple",
          "Stars"
        ],
        hint: "A means of communication between heaven and earth",
        funFact: "👼 Jacob's ladder symbolizes the connection between Heaven and Earth!"
      },
      medium: {
        question: "What did Jacob do with the stone he used as a pillow?",
        options: [
          "He broke it",
          "He buried it",
          "He set it up and anointed it with oil",
          "He took it with him"
        ],
        hint: "He made it a sacred monument",
        funFact: "🪨 This stone becomes 'Bethel' - the house of God!"
      },
      hard: {
        question: "How does this vision connect heaven and earth in our life?",
        options: [
          "Through dreams only",
          "Through prayer that establishes the bridge with the divine",
          "Through guardian angels",
          "Through sacred places"
        ],
        hint: "It's about our communication with God",
        funFact: "🌉 Prayer is our personal ladder to Heaven!"
      }
    },

    10: {
      name: "Joseph Sold by His Brothers",
      challenge: "Here comes the dreamer...",
      easy: {
        question: "How many brothers did Joseph have?",
        options: ["7 brothers", "10 brothers", "11 brothers", "12 brothers"],
        hint: "One less than the number of tribes of Israel",
        funFact: "👨‍👨‍👦‍👦 Jacob's 12 sons will become the 12 tribes of Israel!"
      },
      medium: {
        question: "For how many pieces of silver was Joseph sold?",
        options: [
          "10 pieces",
          "20 pieces",
          "30 pieces",
          "50 pieces"
        ],
        hint: "The price of a slave",
        funFact: "💰 20 pieces of silver, the price of a slave at that time!"
      },
      hard: {
        question: "How does jealousy become an instrument of God's plan?",
        options: [
          "God approves of jealousy",
          "Evil committed is transformed into good by God",
          "Jealousy is necessary",
          "The brothers were right"
        ],
        hint: "It's about divine providence transforming evil",
        funFact: "🎨 God transforms even our worst actions into a masterpiece of grace!"
      }
    },

    11: {
      name: "Joseph in Prison",
      challenge: "The Lord was with Joseph and extended kindness to him...",
      easy: {
        question: "What special gift did Joseph have?",
        options: [
          "Prophesying",
          "Interpreting dreams",
          "Healing the sick",
          "Talking to animals"
        ],
        hint: "An ability related to dreams",
        funFact: "💭 Dreams were considered divine messages in antiquity!"
      },
      medium: {
        question: "Which servants of Pharaoh were in prison with Joseph?",
        options: [
          "The vizier and the scribe",
          "The cupbearer and the baker",
          "The doctor and the guard",
          "The cook and the gardener"
        ],
        hint: "The one who served wine and the one who served bread",
        funFact: "🍷 The cupbearer tasted the king's wine to prevent poisoning!"
      },
      hard: {
        question: "What does Joseph teach us about faithfulness in adversity?",
        options: [
          "Injustice is acceptable",
          "Faithfulness to God transcends circumstances",
          "Prison changes people",
          "We must take revenge"
        ],
        hint: "It's about remaining faithful even in trials",
        funFact: "⭐ Even in prison, Joseph remained upright and God exalted him!"
      }
    },

    12: {
      name: "Pharaoh's Dreams",
      challenge: "Seven fat cows... seven lean cows...",
      easy: {
        question: "How many lean cows devoured the fat cows?",
        options: ["5", "6", "7", "8"],
        hint: "A perfect number",
        funFact: "🐄 The number 7 symbolizes perfection and completeness in the Bible!"
      },
      medium: {
        question: "What did the 7 years in the dream mean?",
        options: [
          "7 years of war then peace",
          "7 years of plenty then 7 years of famine",
          "7 years of sickness then health",
          "7 years of rain then drought"
        ],
        hint: "Cycle of abundance and lack",
        funFact: "🌾 Joseph saved Egypt and his family through this interpretation!"
      },
      hard: {
        question: "How does divine wisdom transform crises?",
        options: [
          "By avoiding all crises",
          "By giving foresight to prepare for them",
          "By punishing the wicked",
          "By enriching believers"
        ],
        hint: "It's about preparation and anticipation",
        funFact: "💡 Divine wisdom transforms crises into opportunities for salvation!"
      }
    },

    13: {
      name: "The Reconciliation of Brothers",
      challenge: "I am Joseph, your brother, whom you sold...",
      easy: {
        question: "What did Joseph put in Benjamin's sack?",
        options: [
          "Gold",
          "His ring",
          "His silver cup",
          "A letter"
        ],
        hint: "A precious personal object",
        funFact: "🏆 The cup was also used to 'read' the future in water!"
      },
      medium: {
        question: "What famous phrase did Joseph say about evil transformed?",
        options: [
          "I forgive you",
          "You meant evil against me, but God turned it into good",
          "Let's forget the past",
          "Vengeance belongs to God"
        ],
        hint: "A phrase about divine providence",
        funFact: "✨ This phrase sums up all of God's providence in our lives!"
      },
      hard: {
        question: "How does forgiveness break cycles of vengeance?",
        options: [
          "By forgetting evil",
          "By freeing both offender and offended for a new beginning",
          "By punishing first",
          "By keeping distance"
        ],
        hint: "It's about mutual liberation",
        funFact: "🕊️ Joseph's forgiveness prefigures universal divine forgiveness!"
      }
    }
  }
};

export default chapter1Translations;
